import { createSlice } from '@reduxjs/toolkit';
import {
  fetchLineChartScanCount,
  fetchListingCountryTable,
  fetchMapScanCount,
  fetchPieChartScanValidity,
  getAllCompanies,
  fetchListingCompanyTable,
  fetchTop20Companies,
  fetchCompanyGenerateTable,
  fetchCompanyAssignedTable,
  fetchGeneratedAssignedGraph,
} from '../action/dashboard.action';

const initialState = {
  mapLoaded: false,
  mapScans: {
    data: [],
    isLoading: true,
    error: null,
  },
  lineChartData: {
    data: [],
    isLoading: true,
    error: null,
  },
  generatedAssignedGraph: {
    isLoading: true,
    data: [],
    error: null,
  },
  pieChartData: {
    totalCount: 0,
    totalSuspended: 0,
    isLoading: true,
    error: null,
  },
  table: {
    data: [],
    isLoading: true,
    error: null,
  },
  companyTable: {
    data: [],
    isLoading: true,
    error: null,
  },
  dateRangeFilter: {
    startDate: null,
    endDate: null,
  },
  top20Companies: [],
  companyGenerateRecords: {
    data: [],
    isLoading: true,
    error: null
  },
  companyAssignedRecords: {
    data: [],
    isLoading: true,
    error: null
  },
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setMapLoaded(state) {
      state.mapLoaded = true;
    },
  },
  extraReducers: {
    // 👇     Map Scan Count     👇 //
    [fetchMapScanCount.pending]: (state) => {
      state.mapScans.isLoading = true;
      state.mapScans.error = null;
    },
    [fetchMapScanCount.fulfilled]: (state, action) => {
      const { payload } = action || {}; // Add a default empty object

      if (payload && payload.data) { // Check if count is an array
        if (Array.isArray(payload.data.Count && payload.data.Suspended)) {
          const tempArray = []; // Initialize tempArray as an empty array

          payload.data.Count.forEach((item) => {
            tempArray.push(item);
          });

          payload.data.Suspended.forEach((item) => {
            tempArray.push(item);
          });

          // update state with tempArray
          state.mapScans.data = tempArray;
        } else {
          state.mapScans.data = [];
        }
      } else {
        state.mapScans.data = [];
      }
      state.mapScans.isLoading = false;
    },
    [fetchMapScanCount.rejected]: (state, action) => {
      state.mapScans.isLoading = false;
      state.mapScans.error = action.error.message;
    },
    // 👇     Line Chart Scan Count     👇 //
    [fetchLineChartScanCount.pending]: (state) => {
      state.lineChartData.isLoading = true;
      state.lineChartData.error = null;
    },
    [fetchLineChartScanCount.fulfilled]: (state, action) => {
      const { payload } = action || {};

      if (payload && payload.data) {
        // Initialize an array to hold data for each date
        const dataArray = [];

        // Iterate through the dates
        Object.keys(payload.data).forEach((date) => {
          const dateData = payload.data[date];

          // Create an object for each date with data for all entities
          const dateDataObject = {
            date,
            ...dateData,
          };

          dataArray.push(dateDataObject);
        });

        state.lineChartData.data = dataArray;
      } else {
        state.lineChartData.data = [];
      }

      state.lineChartData.isLoading = false;
    },
    [fetchLineChartScanCount.rejected]: (state, action) => {
      state.lineChartData.isLoading = false;
      state.lineChartData.error = action.error.message;
    },

    // 👇     Listing Country Table     👇 //
    [fetchListingCountryTable.pending]: (state) => {
      state.table.isLoading = true;
      state.table.error = null;
    },
    [fetchListingCountryTable.fulfilled]: (state, action) => {
      const { payload } = action || {}; // Add a default empty object

      if (payload && Array.isArray(payload.data)) { // Check if payload is an array
        const tempArray = payload.data.map((item) => ({
          country: item.country,
          totalCount: item.total_count,
          totalSuspended: item.total_suspended,
        }));

        state.table.data = tempArray; // Use state.table.data instead of state.table
      } else {
        state.table.data = [];
      }

      state.table.isLoading = false;
    },
    [fetchListingCountryTable.rejected]: (state, action) => {
      state.table.isLoading = false;
      state.table.error = action.error.message;
    },
    // 👇     Listing Company Table     👇 //
    [fetchListingCompanyTable.pending]: (state) => {
      state.companyTable.isLoading = true;
      state.companyTable.error = null;
    },
    [fetchListingCompanyTable.fulfilled]: (state, action) => {
      const { payload } = action || {};

      if (payload && Array.isArray(payload.data)) {
        const tempArray = payload.data.map((item) => ({
          company: item.company,
          totalCount: item.total_count,
          totalSuspended: item.total_suspended,
        }));

        state.companyTable.data = tempArray;
      } else {
        state.companyTable.data = [];
      }

      state.companyTable.isLoading = false;
    },
    // 👇     Pie Chart Scan Validity     👇 //
    [fetchPieChartScanValidity.pending]: (state) => {
      state.pieChartData.isLoading = true;
      state.pieChartData.error = null;
    },
    [fetchPieChartScanValidity.fulfilled]: (state, action) => {
      // eslint-disable-next-line camelcase
      const { payload } = action; // Set default value to an empty object

      state.pieChartData.totalCount = payload.data.total_count;
      state.pieChartData.totalSuspended = payload.data.total_suspended;

      state.pieChartData.isLoading = false;
    },
    [fetchPieChartScanValidity.rejected]: (state, action) => {
      state.pieChartData.isLoading = false;
      state.pieChartData.error = action.error.message;
    },
    // 👇     Companies Data     👇 //
    [getAllCompanies.fulfilled]: (state, action) => {
      const { payload } = action;
      const tempArray = [];
      state.totalRecords = payload.recordsTotal;
      state.isLoadingCompanyError = false;
      payload.data.forEach((company) => {
        tempArray.push({
          id: company.id,
          name: company.name,
        });
      });
      state.allCompanies = tempArray;
    },
    [fetchTop20Companies.fulfilled]: (state, action) => {
      const { payload } = action;

      if (payload && Array.isArray(payload)) {
        const tempArray = payload.map((company) => company.id);
        state.top20Companies = tempArray;
      } else {
        state.top20Companies = [];
      }
    },
    // 👇     Company Generate Table     👇 //
    [fetchCompanyGenerateTable.pending]: (state) => {
      state.companyGenerateRecords.isLoading = true;
      state.companyGenerateRecords.error = null;
    },
    [fetchCompanyGenerateTable.fulfilled]: (state, action) => {
      const { payload } = action;
      state.companyGenerateRecords.data = payload.data;
      state.companyGenerateRecords.isLoading = false;
    },
    [fetchCompanyGenerateTable.rejected]: (state, action) => {
      state.companyGenerateRecords.isLoading = false;
      state.companyGenerateRecords.error = action.error.message;
    },
    // 👇     Company Assigned Table     👇 //
    [fetchCompanyAssignedTable.pending]: (state) => {
      state.companyAssignedRecords.isLoading = true;
      state.companyAssignedRecords.error = null;
    },
    [fetchCompanyAssignedTable.fulfilled]: (state, action) => {
      const { payload } = action;
      state.companyAssignedRecords.data = payload.data;
      state.companyAssignedRecords.isLoading = false;
      state.companyAssignedRecords.error = null;
    },
    [fetchCompanyAssignedTable.rejected]: (state, action) => {
      state.companyAssignedRecords.isLoading = false;
      state.companyAssignedRecords.error = action.error.message;
    },
    // 👇     Generated Assigned Graph     👇 //
    [fetchGeneratedAssignedGraph.pending]: (state) => {
      state.generatedAssignedGraph.isLoading = true;
      state.generatedAssignedGraph.error = null;
    },
    [fetchGeneratedAssignedGraph.fulfilled]: (state, action) => {
      const { payload } = action;
      state.generatedAssignedGraph.data = payload.data;
      state.generatedAssignedGraph.isLoading = false;
      state.generatedAssignedGraph.error = null;
    },
    [fetchGeneratedAssignedGraph.rejected]: (state, action) => {
      state.generatedAssignedGraph.isLoading = false;
      state.generatedAssignedGraph.error = action.error.message;
      state.generatedAssignedGraph.isLoading = false;
    },
  },
});

export const { setMapLoaded, updateDateRangeFilter } = dashboardSlice.actions;
export const dashboardReducer = dashboardSlice.reducer;
